import { BROSWER } from "./globals";
import { getCookie } from "./cookie-util";

/**
 * @param {Array<string>} array
 * @param {String} separtor - The string each item is separated by.
 * @param {String} defaultValue - The default null value.
 * @param {String} [property] - The optional property name its targeting within the array.
 * @returns {String} Returns either the defaultValue or a string for analytics.
 */
export const joinArray = (array, separator, defaultValue, property) => {
  if (!array || array.length === 0) {
    return defaultValue;
  }
  const stringArray = property ? array.map(x => x[property]) : array;
  return stringArray.join(separator);
};

/**
 * Wraps specified function with button keyboard logic
 * i.e. only " " (Space) or "Enter" keydowns will trigger the function.
 *
 * @param {Object} event The keyboard event
 * @param {Array<String>} keys The keys that the function is triggered
 * @param {Function} callback The function to be wrapped
 * @returns {Object} The wrapped function
 */
export const keyDownButtonWrapper = (event, keys, callback) => {
  if (keys.some(key => event.key === key)) {
    event.preventDefault();
    callback();
  }
};

export const generateGradient = ({ topPercent, bottomPercent }) => {
  const topGradient = topPercent
    ? `linear-gradient(to bottom, rgba(0, 0, 0, 0.75) 3%, rgba(0, 0, 0, 0) ${topPercent}%)`
    : null;

  const bottomGradient = bottomPercent
    ? `linear-gradient(to top, rgba(0, 0, 0, 0.75) 3%, rgba(0, 0, 0, 0) ${bottomPercent}%)`
    : null;

  return [topGradient, bottomGradient].filter(Boolean).join();
};

/**
 * @description Get existing script from DOM matching source URL
 * @param {string} source - Source URL
 * @returns {Object}
 */
export function getScript(source) {
  return window.document.querySelector(`script[src="${source}"]`);
}

/**
 * @description Listen to existing script in DOM
 * @param {string} script - Script element
 * @param {Function} onLoad - Callback run on script load
 * @param {Function} onError - Callback run on script error
 * @returns {void}
 */
export function listenScriptOnce(script, onLoad, onError) {
  script.addEventListener("load", onLoad, { once: true });
  script.addEventListener("error", onError, { once: true });
}

/**
 * @description Load new script into DOM with source URL
 * @param {string} source - Source URL
 * @param {Function} onLoad - Callback run on script load
 * @param {Function} onError - Callback run on script error
 * @returns {void}
 */
export function loadScript(source, onLoad, onError) {
  if (typeof document === "undefined") return;
  const script = document.createElement("script");
  script.src = source;
  script.async = true;
  script.onload = () => onLoad?.();
  script.onerror = () => onError?.(`Failed to load script: ${source}`);
  document.body.appendChild(script);
}

export const getBrowserType = () => {
  const userAgent = window?.navigator?.userAgent || "";

  const browserMap = {
    [BROSWER.OPERA]:
      userAgent.includes(BROSWER.OPERA) || userAgent.includes("OPR"),
    [BROSWER.EDGE]: userAgent.includes("Edg"),
    [BROSWER.CHROME]: userAgent.includes(BROSWER.CHROME),
    [BROSWER.SAFARI]: userAgent.includes(BROSWER.SAFARI),
    [BROSWER.FIREFOX]: userAgent.includes(BROSWER.FIREFOX)
  };
  return Object.keys(browserMap).find(key => browserMap[key]) || "";
};

export const getDeviceTypeAkamai = () => {
  // we trust akamai supplied devicetype if it exists
  const wapoDeviceTypeCookie = getCookie("wp_devicetype");
  if (wapoDeviceTypeCookie) {
    return wapoDeviceTypeCookie === "2"
      ? "tablet"
      : wapoDeviceTypeCookie === "1"
      ? "mobile"
      : "desktop";
  }
  // otherwise we default to trusting navigator.userAgent
  const userAgent = getUserAgent();
  const isIOS = /ipod|iphone|ipad/i.test(userAgent);
  const isTablet = /tablet|ipad/i.test(userAgent);
  // Check for iOS included here to accomodate iOS WKWebViews with custom app names, which do not have "Mobile" in their UA strings
  const isMobile = !isTablet && (isIOS || /[^-]mobi/i.test(userAgent));
  if (isTablet) return "tablet";
  if (isMobile) return "mobile";

  return "desktop";
};

export const getUserAgent = () => {
  if (typeof window === "undefined") return "";
  return window?.navigator?.userAgent ? navigator.userAgent : "";
};

export function bound(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function addEventListeners(element, listeners) {
  listeners.forEach(([event, callback]) => {
    element.addEventListener(event, callback);
  });
}

export function removeEventListeners(element, listeners) {
  listeners.forEach(([event, callback]) => {
    element.removeEventListener(event, callback);
  });
}
